/**
 * Phase 4e — `NumericSpanFormatter`, the read-only half of
 * `fn.uni_tools.bindFormatter` (`django/static/mvc/universal_fn/formatter.js`).
 *
 * The selection popup formats THREE outputs through it (sizer_popup.js:580-587,
 * :655-662): the Supply Airflow readout (`data-formatter_type="integer"`,
 * sizer_popup.tmpl:40) and each tunnel's current width/height
 * (`data-formatter_type="decimal"`, sizer_popup.js:624). OBSERVED results on
 * AHU 3833: `20,000` and `100.00 x 100.00`.
 *
 * This is NOT `formatLegacyNumber` (`pages/ahu-board/forms/formUtils.ts`): that
 * one ports `validateNumberField` from the sizer PAGE, which rounds
 * (`Math.round` / `toFixed(2)` keyed off `data-step`) and clamps to
 * `data-min`/`data-max`. The formatter's `encode` has a different decode step
 * and a different mode vocabulary, so the two are kept separate.
 */

export type FormatterMode = 'integer' | 'decimal' | 'float';

/**
 * `BaseNumericFormatter.decode` (formatter.js:101-126) — strip everything that
 * is not a digit, `.` or `-`, dropping leading zeros before the first digit and
 * any `-` that is not first, then `parseFloat`.
 *  - an empty string returns `undefined` ("returning null if the content is
 *    empty"), which is why `setValue`'s `if (value)` then skips formatting;
 *  - a string that reduces to `''`, `'.'` or `'-'` returns 0.
 */
export function formatterDecode(encoded: unknown): number | undefined {
  const input = (encoded == null ? '' : encoded).toString();
  if (input === '') return undefined;
  let res = '';
  let numbersStarted = false;
  for (const c of input) {
    const isNumeric = c !== ' ' && !Number.isNaN(Number(c));
    if (isNumeric || c === '.' || c === '-') {
      if (!numbersStarted && c && isNumeric) numbersStarted = true;
      if (c === '0' && !numbersStarted) continue;
      if (c === '-' && res) continue;
      res += c;
    }
  }
  if (!res || res === '.' || res === '-') return 0;
  return parseFloat(res);
}

/**
 * `BaseNumericFormatter.encode` (formatter.js:127-180): comma-group the integer
 * part, then append a mode-sized fraction — 0 digits for `integer`, 2 for
 * `decimal`, 4 for `float`. (The `element === document.activeElement`
 * short-circuit at :128 cannot apply to a `<span>`.)
 */
export function formatterEncode(decoded: number | null | undefined, mode: FormatterMode): string {
  if (decoded == null) return '';
  let value = decoded;
  let isNegative = value < 0;
  if (isNegative) value = -value;

  const parts = value.toString().split('.');
  let leftPart = parts[0]
    .split('')
    .reverse()
    .map((e, i, symbols) => ((i + 1) % 3 === 0 && i !== symbols.length - 1 ? `,${e}` : e))
    .reverse()
    .join('');

  let rightPart = '';
  if (mode === 'decimal' && !Number.isNaN(value)) rightPart = value.toFixed(2).split('.')[1];
  else if (mode === 'float' && !Number.isNaN(value)) rightPart = value.toFixed(4).split('.')[1];

  if (!leftPart) {
    if (rightPart) leftPart = '0';
    else isNegative = false; // -0 => 0
  }
  return `${isNegative ? '-' : ''}${leftPart}${rightPart ? `.${rightPart}` : ''}`;
}

/**
 * `NumericSpanFormatter.setValue(element.innerText)` (formatter.js:255-269) — the
 * whole binding, as a pure function.
 *
 * ⚠ `if (value)` at :256 means a decoded 0 (and `undefined`, and `''`) leaves
 * the span's ORIGINAL text untouched: an airflow of `0` renders as the raw
 * `"0"`, never `"0"`-through-encode. Reproduced by returning the raw input.
 */
export function formatSpan(raw: unknown, mode: FormatterMode): string {
  const decoded = formatterDecode(raw);
  if (!decoded) return raw == null ? '' : String(raw);
  return formatterEncode(decoded, mode);
}
